import { z } from 'zod';
import { env } from '@/env';
import { isAuthFailure } from '@/shared/auth-failure';
import { getAuthToken, redirectToLogin } from '@/shared/auth-token';
import { reportStorageModuleRefusal, STORAGE_MODULE_OFF_MESSAGE } from '@/shared/storage-module';
import {
  ConfirmUploadResultSchema,
  ListStorageDocumentsResultSchema,
  RegisterStorageResultSchema,
  StorageProfileSchema,
  UploadDocumentResultSchema,
  UpdateStorageProfileResultSchema,
  type ApprovalStatus,
  type ConfirmUploadResult,
  type ListStorageDocumentsResult,
  type RegisterStorageResult,
  type StorageDocument,
  type StorageDocumentStatus,
  type StorageDocumentType,
  type StorageNextStep,
  type StorageProfile,
  type UpdateStorageProfileResult,
  type UploadDocumentResult
} from '@/contracts/storage-profile';
import {
  BankAccountOptionsSchema,
  StorageBankAccountResponseSchema,
  StorageBankAccountSchema,
  type BankCatalogEntry,
  type StorageBankAccount
} from '@/contracts/storage-bank-account';

export type {
  ApprovalStatus,
  ConfirmUploadResult,
  RegisterStorageResult,
  StorageDocument,
  StorageDocumentStatus,
  StorageDocumentType,
  StorageNextStep,
  StorageProfile,
  UpdateStorageProfileResult,
  UploadDocumentResult,
  BankCatalogEntry,
  StorageBankAccount
};

/**
 * Types and calls for `GET/PATCH /storage/me` and the `/storage/documents/*` trio (roadmap §2), and
 * since NEX-1169 `GET/PATCH /storage/me/bank-account` plus the bank catalog (end of this file).
 *
 * Field names and shapes here are not a guess from the roadmap's terse endpoint list alone: this
 * milestone (`nexvia-storage-web-app`, Milestone 2) runs alongside `nexvia-api-web`'s own
 * Milestone 2, and its real (uncommitted, in-progress) handlers were read directly —
 * `get-storage-profile.handler.ts`, `update-storage-profile.handler.ts`,
 * `storage.schemas.ts`, `storage-profile.entity.ts` — rather than mirrored secondhand from
 * `driver-document`. Where that module's own Milestone 3 (the documents handlers themselves)
 * is not built yet, the `storage_documents` migration's real column/enum names
 * (`1788900000000-CreateStorageDocuments.js`) are used instead, with `driver-document`'s actual
 * handlers (`request-upload-url.handler.ts`, `confirm-upload.handler.ts`) as the shape mirror for
 * the two responses that module doesn't have a storage-specific handler for yet.
 *
 * DELIBERATELY SELF-CONTAINED — does not import a shared `@/shared/api`/`@/shared/apiClient`.
 * This repo's milestones ran as concurrent agents against the same working tree with no
 * file-level isolation: a generic HTTP-transport module under one of those two names was written,
 * overwritten and rewritten by more than one other concurrent milestone while this one was in
 * progress (`src/shared/storage-services-api.ts`'s own docblock documents the same finding
 * independently). Depending on either here would make this module's correctness hostage to a file
 * this milestone does not own and that may still be mid-rewrite by the time this one finishes —
 * see this milestone's final report for the concurrency finding in full. `TOKEN_STORAGE_KEY` from
 * `@/shared/useAuth` is a different kind of dependency (one specific, already-finished module,
 * not a same-named transport every milestone might independently reinvent) and stays.
 *
 * Once the three repos' agents (or a human) reconcile on ONE shared transport module, the
 * `request`/`ApiError`/token-reading block below is a mechanical delete-and-reimport away from
 * using it.
 *
 * **Update, 2026-10-14 (later the same day)**: the token-reading piece specifically has now been
 * shared after all — `@/shared/auth-token.ts` is the one helper every transport module (this one
 * included) asks for a live token plus a 401/403 retry-then-redirect, replacing the direct
 * `localStorage.getItem(TOKEN_STORAGE_KEY)` read this file used to do (the paragraph above's claim
 * that `TOKEN_STORAGE_KEY` "stays" as a direct dependency no longer describes this file —
 * `auth-token.ts` is now the one place that constant is read from, for every module that shares it).
 * The REQUEST/ENVELOPE/SCHEMA logic (`request()`, `unwrapEnvelope`, the Zod parse, `ApiError`) stays
 * exactly as self-contained as described above — only the token-and-retry slice was in scope for
 * sharing today.
 *
 * Phase 3.1 (`docs/superpowers/plans/2026-10-14-harness/roadmap.md`): every response this module
 * returns now parses through a `src/contracts/storage-profile.ts` Zod schema instead of a
 * hand-written interface + manual field access — see that file for the real ground truth each
 * schema was read from, and `request()` below for the one new behavior this adds: a 2xx body that
 * fails its schema is caught and re-thrown as this file's own `ApiError` (`code:
 * 'CONTRACT_VIOLATION'`), never left to escape as a raw `ZodError` and crash the calling page the
 * way `ff309b7`'s envelope bug once did.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

function parseJsonSafely(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Confirmed shape (not guessed): `nexvia-api-web/src/shared/middlewares/error.middleware.ts`
 *  always answers an operational error as `{ success: false, message, code? }`. */

/**
 * Correction (re-verified directly against the live `storage.controller.ts`, 2026-10-14): `GET`/
 * `PATCH /storage/me` are BARE (`res.status(200).json(result)`), NOT `{success,data}` — this
 * docblock previously claimed the opposite, which was wrong. The endpoints that DO wrap as
 * `{success:true,data}` are the `/storage/locations` CRUD, `/storage/service-options` and
 * `/storage/me/services` (a different controller method than `getMe`/`updateMe`, despite the
 * similar path). `POST /auth/register-storage` and `/storage/documents/*` are also bare
 * (auth.controller.ts's `{...result, phoneVerified, role}`, storage-document's controller's
 * `res.json(result)`). `unwrapEnvelope` below is still correct to keep: it detects by shape (BOTH
 * `success` and `data` keys present) rather than by path, so it's a no-op on every bare response
 * here — including `updateStorageProfile`'s own `{success,message,storageProfile}` result, which
 * has a `success` key but no `data` key and so correctly passes through untouched.
 */
function unwrapEnvelope<T>(body: unknown): T {
  if (body && typeof body === 'object' && !Array.isArray(body) && 'success' in body && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

interface RawResponse {
  ok: boolean;
  status: number;
  body: unknown;
}

async function sendRequest(path: string, init: RequestInit | undefined, token: string | null): Promise<RawResponse> {
  // `new URL(path, base)` rather than plain concatenation: VITE_API_URL carries a trailing slash
  // (same convention as nexvia-web-app's own .env), and `path` starts with one too — string
  // concatenation produced a literal `//` that reached the network as part of the URL.
  const response = await fetch(new URL(path, env.VITE_API_URL).toString(), {
    ...init,
    headers: {
      Accept: 'application/json',
      // Only a JSON string: a `FormData` body (the document upload) sets its own multipart boundary.
      ...(typeof init?.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers
    }
  });

  const body = parseJsonSafely(await response.text());
  return { ok: response.ok, status: response.status, body };
}

function toApiError(path: string, raw: RawResponse): ApiError {
  const record = raw.body && typeof raw.body === 'object' ? (raw.body as Record<string, unknown>) : null;
  const message = typeof record?.message === 'string' ? record.message : `Request to ${path} failed with status ${raw.status}`;
  const code = typeof record?.code === 'string' ? record.code : undefined;
  return new ApiError(message, raw.status, code);
}


/**
 * A 2xx body that doesn't match the endpoint's schema (a malformed/unexpected real response) is
 * caught here and re-thrown as this module's own `ApiError` instead of letting the raw `ZodError`
 * escape to the calling page — that would crash the page exactly the way the original `(current ??
 * []) is not iterable` / `options.map is not a function` bugs did, the one failure mode Phase 3
 * exists to prevent, not reintroduce in a new shape. `code: 'CONTRACT_VIOLATION'` is a stable,
 * dedicated code existing page-level error handling (already built around catching `ApiError`)
 * degrades to the existing error banner for, the same as any other mapped backend error code.
 *
 * **Fixed, 2026-10-14**: a 401 or 403 (nexvia-api-web answers an expired-Firebase-token failure on
 * `/storage/*` routes with 403, not 401 — confirmed against this repo's own backend logs) now
 * forces one live token refresh (`@/shared/auth-token`'s `getAuthToken(true)`) and replays the exact
 * same request once before giving up; anything still wrong after that single replay — no refreshed
 * token to retry with, or the replay is *also* 401/403 — calls `redirectToLogin()` and throws the
 * same `ApiError` shape this module has always thrown, same as any other non-2xx status. This retry
 * check runs before the schema parse below, so `describeStorageProfileError`'s own business-error-
 * code mapping (unrelated to auth) is untouched — it only ever sees the resulting `ApiError`, same
 * as before.
 */
async function request<T>(path: string, init: RequestInit | undefined, schema: z.ZodType<T>): Promise<T> {
  const token = await getAuthToken();
  let raw = await sendRequest(path, init, token);

  // NEX-1164 — only a session failure (401, 403 AUTH_*) retries/redirects; a 403 domain refusal

  // (STORAGE_NOT_APPROVED, STORAGE_ROLE_REQUIRED, ...) falls through to the page as a plain ApiError.

  if (isAuthFailure(raw.status, raw.body)) {
    const refreshedToken = await getAuthToken(true);
    if (refreshedToken) {
      raw = await sendRequest(path, init, refreshedToken);
    }
    if (!refreshedToken || isAuthFailure(raw.status, raw.body)) {
      redirectToLogin();
      throw toApiError(path, raw);
    }
  }

  if (!raw.ok) {
    const error = toApiError(path, raw);
    reportStorageModuleRefusal(error.status, error.code);
    throw error;
  }

  const unwrapped = unwrapEnvelope<unknown>(raw.body);
  try {
    return schema.parse(unwrapped);
  } catch (err) {
    if (err instanceof z.ZodError) {
      const issues = err.issues.map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`).join('; ');
      // 502, not raw.status (always a real 2xx here) — matches the convention the other three
      // Phase 3 migrations independently converged on (storage-api.ts, storage-locations-api.ts,
      // storage-services-api.ts): a 2xx that fails its own contract reads as an upstream contract
      // violation, not as whatever 2xx code it happened to arrive with.
      throw new ApiError(`Unexpected response shape from ${path} (${issues})`, 502, 'CONTRACT_VIOLATION');
    }
    throw err;
  }
}

const apiGet = <T>(path: string, schema: z.ZodType<T>): Promise<T> => request(path, { method: 'GET' }, schema);
const apiPost = <T>(path: string, schema: z.ZodType<T>, payload?: unknown): Promise<T> =>
  request(path, { method: 'POST', body: payload !== undefined ? JSON.stringify(payload) : undefined }, schema);
const apiPatch = <T>(path: string, schema: z.ZodType<T>, payload?: unknown): Promise<T> =>
  request(path, { method: 'PATCH', body: payload !== undefined ? JSON.stringify(payload) : undefined }, schema);

// --- Profile -----------------------------------------------------------------------------------

export function getStorageProfile(): Promise<StorageProfile> {
  return apiGet('/storage/me', StorageProfileSchema);
}

/**
 * NEX-1164 — the one answer that means "this login has no depósito account yet":
 * `get-storage-profile.handler.ts` (and `PATCH /storage/me`, and the quotes gate) throw 404
 * `STORAGE_PROFILE_NOT_FOUND` for exactly that case. Matched by code, not by status alone, so a
 * mis-routed request (a 404 for a path that doesn't exist) is never mistaken for it.
 */
export function isStorageProfileMissing(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404 && err.code === 'STORAGE_PROFILE_NOT_FOUND';
}

/**
 * NEX-1164 — whether the signed-in login already has a depósito account. `false` sends it to
 * `/registro` (`Login.tsx`, `EmpresaPage`); any other failure is rethrown for the caller to decide.
 * Replaces `ensureStorageProfile`, which used to CREATE the account here with placeholder data
 * (RUT `000000000000`, phone `00000000`, "(pendiente de completar)") — business doc §5 asks for the
 * real RUT and razón social at sign-up, so nothing is created without the registration form.
 */
export async function hasStorageProfile(): Promise<boolean> {
  try {
    await getStorageProfile();
    return true;
  } catch (err) {
    if (isStorageProfileMissing(err)) return false;
    throw err;
  }
}

/**
 * `POST /auth/register-storage`'s body — `registerStorageBody` (auth.schemas.ts) as the NEX-1164
 * handler reads it: on the first call `razonSocial`, a 12-digit `rut` and `phone` are required;
 * `contactEmail`/`contactPhone` default to the login email / that phone when absent. The schema's
 * `email` key is accepted and ignored by the API (the login email is the Firebase identity), so
 * this app never sends it.
 */
export interface RegisterStorageInput {
  razonSocial: string;
  rut: string;
  phone: string;
  contactEmail?: string | null;
  contactPhone?: string | null;
}

export function registerStorage(input: RegisterStorageInput): Promise<RegisterStorageResult> {
  return apiPost('/auth/register-storage', RegisterStorageResultSchema, input);
}

/**
 * `PATCH /storage/me`'s body — exactly `updateStorageMeBody` (storage.schemas.ts): no `email`.
 * NEX-1164: `contactEmail`/`contactPhone` are the quote-request contact; `null` clears either one
 * (the API then falls back to the login email/phone).
 */
export interface UpdateStorageProfileInput {
  razonSocial?: string;
  rut?: string;
  phone?: string;
  contactEmail?: string | null;
  contactPhone?: string | null;
}

export function updateStorageProfile(input: UpdateStorageProfileInput): Promise<UpdateStorageProfileResult> {
  return apiPatch('/storage/me', UpdateStorageProfileResultSchema, input);
}

/**
 * Error codes thrown by `update-storage-profile.handler.ts` and, since NEX-1164, by
 * `register-storage.ts` — both through `nexvia-api-web`'s `domain/storage-profile-fields.ts`, read
 * verbatim off that file (`*_REQUIRED` is the registration's blank-value code, `*_INVALID` the
 * edit's; `VALIDATION_FIELD_TOO_LONG` is its column-width check).
 */
const PROFILE_ERROR_MESSAGES: Record<string, string> = {
  STORAGE_RUT_INVALID: 'El RUT tiene que tener exactamente 12 dígitos.',
  STORAGE_RUT_ALREADY_REGISTERED: 'Ese RUT ya está registrado en otra cuenta de depósito.',
  STORAGE_RAZON_SOCIAL_INVALID: 'La razón social no puede quedar vacía.',
  STORAGE_RAZON_SOCIAL_REQUIRED: 'Ingresá la razón social de tu empresa.',
  STORAGE_PHONE_INVALID: 'El teléfono no puede quedar vacío.',
  STORAGE_PHONE_REQUIRED: 'Ingresá un teléfono de contacto.',
  STORAGE_CONTACT_EMAIL_INVALID: 'El correo para cotizaciones no es válido.',
  STORAGE_CONTACT_PHONE_INVALID: 'El teléfono para cotizaciones no es válido.',
  VALIDATION_FIELD_TOO_LONG: 'Uno de los datos es demasiado largo. Acortalo e intentá de nuevo.',
  STORAGE_NO_VALID_FIELDS: 'No hay cambios para guardar.',
  // Audit S6 — `PATCH /storage/me` on a login whose depósito account is gone (`update-storage-profile.handler.ts`).
  STORAGE_PROFILE_NOT_FOUND:
    'No encontramos tu cuenta de depósito. Recargá la página; si sigue pasando, escribinos a soporte@nexvia.io.',
  // NEX-1164 (W2-hard-b) — a RUT/razón social change found the account in another status than it
  // read (e.g. Nexvia deciding on it at that moment): nothing was saved.
  STORAGE_ACCOUNT_STATUS_TRANSITION_INVALID:
    'El estado de tu cuenta cambió mientras guardabas (por ejemplo, Nexvia la estaba revisando) y no se guardó nada. Recargá la página y probá de nuevo.',
  // NEX-1645 — `POST /auth/register-storage` (and `/storage/me`) with the module turned off after
  // `/registro` read `GET /app/features`: 503 from nexvia-api-web's `require-storage-module.middleware.ts`.
  STORAGE_MODULE_DISABLED: STORAGE_MODULE_OFF_MESSAGE
};

const TOO_MANY_REQUESTS_MESSAGE = 'Demasiados intentos. Esperá unos minutos y probá de nuevo.';

/** Mirrors `useAuth.tsx`'s `describeAuthError`: map the backend's machine code to es-UY copy.
 *  `fallback` lets the registration form say "registrar" instead of "guardar los cambios". */
export function describeStorageProfileError(
  err: unknown,
  fallback = 'No pudimos guardar los cambios. Probá de nuevo en un momento.'
): string {
  const code = (err as { code?: string } | null | undefined)?.code;
  const known = code ? PROFILE_ERROR_MESSAGES[code] : undefined;
  if (known) return known;
  // Audit S6 — the API's rate limiter (`rate-limit.middleware.ts`) answers 429, e.g. to repeated
  // registration attempts; matched by status, whatever its code.
  if (err instanceof ApiError && err.status === 429) return TOO_MANY_REQUESTS_MESSAGE;
  return fallback;
}

// --- Documents -------------------------------------------------------------------------------

export function listStorageDocuments(): Promise<ListStorageDocumentsResult> {
  return apiGet('/storage/documents', ListStorageDocumentsResultSchema);
}

/**
 * `POST /storage/documents/upload` (2026-10-28): the file goes to the API, which puts it in Spaces
 * itself. It used to be a presigned URL and a PUT straight to the bucket, which the bucket's CORS
 * refused from the panel's domain in every environment ("No pudimos subir el archivo"). Through the
 * API the browser never talks to Spaces. `confirmDocumentUpload` follows, as before.
 */
export async function uploadDocument(documentType: StorageDocumentType, file: File): Promise<UploadDocumentResult> {
  const body = new FormData();
  body.append('documentType', documentType);
  body.append('file', file);
  try {
    return await request('/storage/documents/upload', { method: 'POST', body }, UploadDocumentResultSchema);
  } catch (err) {
    // fetch rejects (a bare TypeError "Failed to fetch") when the request gets no answer at all.
    if (err instanceof TypeError) throw new ApiError(UPLOAD_NETWORK_ERROR_MESSAGE, 0, UPLOAD_NETWORK_ERROR_CODE);
    throw err;
  }
}

export function confirmDocumentUpload(documentId: number): Promise<ConfirmUploadResult> {
  return apiPatch(`/storage/documents/${documentId}/confirm`, ConfirmUploadResultSchema);
}

const UPLOAD_NETWORK_ERROR_CODE = 'UPLOAD_NETWORK_ERROR';
const UPLOAD_NETWORK_ERROR_MESSAGE = 'No pudimos subir el archivo. Revisá tu conexión y probá de nuevo.';
const UPLOAD_TOO_LARGE_FOR_SERVER_MESSAGE = 'El archivo es demasiado pesado para subirlo. Probá con uno más liviano.';

/**
 * NEX-1164 — the codes a document upload can answer (`storage-document/handlers/request-upload-url`,
 * which also serves `POST /storage/documents/upload`, and `confirm-upload`, nexvia-api-web), plus
 * this module's own network code, whose message is already Spanish. Anything else gets a generic
 * Spanish line: the API's `message` is English and a network failure's is "Failed to fetch", so
 * neither is ever shown.
 */
const DOCUMENT_ERROR_MESSAGES: Record<string, string> = {
  STORAGE_DOC_STATUS_INVALID_TRANSITION:
    'Este documento cambió de estado mientras lo subías. Recargá la página para ver cómo quedó y, si hace falta, subilo de nuevo.',
  STORAGE_DOC_ALREADY_UNDER_REVIEW: 'Este documento ya está en revisión: vas a poder reemplazarlo cuando Nexvia lo apruebe o lo rechace.',
  STORAGE_DOC_FILE_TOO_LARGE: 'El archivo pesa más de 10 MB.',
  // Audit S6 — the rest of what `request-upload-url` / `confirm-upload` answer. (STORAGE_DOC_CONTENT_MISMATCH
  // is gone: the API's validation service raises it internally and confirm-upload answers it as
  // STORAGE_DOC_VALIDATION_FAILED, so it never reached this panel.)
  STORAGE_DOC_FILE_TOO_SMALL: 'El archivo pesa menos de 10 KB: puede que esté vacío o dañado. Elegí otro archivo.',
  VALIDATION_ENUM_INVALID: 'Formato no admitido. Subí un PDF, JPG, PNG, HEIC o WEBP.',
  STORAGE_DOC_NOT_OWNER: 'Este documento no es de tu cuenta. Recargá la página y subilo de nuevo.',
  STORAGE_DOC_PROFILE_NOT_FOUND_FOR_USER:
    'No encontramos tu cuenta de depósito. Recargá la página; si sigue pasando, escribinos a soporte@nexvia.io.',
  STORAGE_DOC_VALIDATION_FAILED: 'No pudimos validar el archivo. Revisá que sea un PDF, JPG o PNG legible y probá de nuevo.',
  // Audit CTR-7 — `POST /storage/documents/upload` without a file (`storage-document.controller.ts`)
  // and multer refusing the multipart (`shared/single-file-upload.ts`: a second file, a wrong field).
  UPLOAD_FILE_REQUIRED: 'Elegí un archivo antes de subir.',
  UPLOAD_FILE_INVALID: 'No pudimos leer el archivo. Probá con otro.',
  // Audit CTR-3 — the module closed after the page loaded.
  STORAGE_MODULE_DISABLED: STORAGE_MODULE_OFF_MESSAGE,
  [UPLOAD_NETWORK_ERROR_CODE]: UPLOAD_NETWORK_ERROR_MESSAGE
};

export function describeDocumentUploadError(err: unknown): string {
  if (err instanceof ApiError) {
    const known = err.code ? DOCUMENT_ERROR_MESSAGES[err.code] : undefined;
    if (known) return known;
    // The web server in front of the API answers 413 (an HTML page, no code) to a body over its own
    // limit, which may sit below the API's 10 MB.
    if (err.status === 413) return UPLOAD_TOO_LARGE_FOR_SERVER_MESSAGE;
  }
  return 'No pudimos subir el archivo. Probá de nuevo en un momento.';
}

// --- Bank account (NEX-1169) -----------------------------------------------------------------

/**
 * NEX-1169 (plan 2026-10-20, Tarea 1.10) — `GET /storage/me/bank-account`: where Nexvia pays the
 * depósito, or `null` while it saved none. Bare, like `GET /storage/me`; the full account number
 * comes back (the panel masks it on screen). See `src/contracts/storage-bank-account.ts`.
 */
export function getStorageBankAccount(): Promise<StorageBankAccount | null> {
  return apiGet('/storage/me/bank-account', StorageBankAccountResponseSchema);
}

/**
 * `PATCH /storage/me/bank-account`'s body — `upsertStorageBankAccountBody` (storage.schemas.ts):
 * eight optional strings, but every one but `bankEmail` is a `NOT NULL` column, so the form always
 * sends all of them. `bankEmail` left out keeps the saved one (TypeORM skips an `undefined` field
 * on update); `''` clears it. `isVerified` is not accepted (the schema strips it).
 */
export interface UpsertStorageBankAccountInput {
  paymentMethod: string;
  bankCode: string;
  bankAccountType: string;
  accountNumber: string;
  beneficiaryName: string;
  documentType: string;
  documentNumber: string;
  bankEmail?: string;
}

/** Creates or replaces the depósito's one bank account; answers the saved row (`bankName: null`). */
export function upsertStorageBankAccount(input: UpsertStorageBankAccountInput): Promise<StorageBankAccount> {
  return apiPatch('/storage/me/bank-account', StorageBankAccountSchema, input);
}

/**
 * The banks the API accepts (`bank_catalog`, Uruguay) — `GET /driver/bank-account/options`, the one
 * endpoint that lists that catalog to a logged-in user: `upsert-storage-bank-account.handler.ts`
 * checks `bankCode` against the same table (`findActiveByCode`). The route has only
 * `authMiddleware`, no carrier check (`driver.routes.ts`). [ASSUMED] a storage-specific options
 * endpoint is not needed while the catalog is shared; if the API adds one, only this path changes.
 */
export async function listBanks(): Promise<BankCatalogEntry[]> {
  const options = await apiGet('/driver/bank-account/options?country=UY', BankAccountOptionsSchema);
  return options.banks;
}

/** What `upsert-storage-bank-account.handler.ts` and `validate()` answer, in es-UY. */
const BANK_ACCOUNT_ERROR_MESSAGES: Record<string, string> = {
  STORAGE_BANK_CODE_INVALID: 'Ese banco no está disponible para recibir pagos. Elegí otro de la lista.',
  RESOURCE_NOT_FOUND:
    'No encontramos tu cuenta de depósito. Recargá la página; si sigue pasando, escribinos a soporte@nexvia.io.',
  VALIDATION_FIELD_INVALID: 'Alguno de los datos no tiene el formato esperado. Revisalos y probá de nuevo.',
  VALIDATION_FIELD_TOO_LONG: 'Uno de los datos es demasiado largo. Acortalo e intentá de nuevo.',
  // Audit CTR-3 — the module closed after the page loaded.
  STORAGE_MODULE_DISABLED: STORAGE_MODULE_OFF_MESSAGE
};

const ACCOUNT_NUMBER_FORMAT_MESSAGE = 'El número de cuenta no tiene el formato de ese banco.';

/**
 * NEX-1169 — `STORAGE_BANK_ACCOUNT_NUMBER_INVALID`'s `message` is the handler's own Spanish rule for
 * that bank ("BROU: 10 dígitos (viejo formato) o 14 dígitos (nuevo formato)", its
 * `BANK_ACCOUNT_RULES`), so it is shown after the generic line — only when it reads like one of
 * those rules (all of them say "dígitos"), never an English message.
 */
export function describeBankAccountError(
  err: unknown,
  fallback = 'No pudimos guardar tu cuenta bancaria. Probá de nuevo en un momento.'
): string {
  if (err instanceof ApiError && err.code === 'STORAGE_BANK_ACCOUNT_NUMBER_INVALID') {
    const rule = err.message.trim();
    return /dígitos/i.test(rule) ? `${ACCOUNT_NUMBER_FORMAT_MESSAGE} ${rule}.` : ACCOUNT_NUMBER_FORMAT_MESSAGE;
  }
  const code = (err as { code?: string } | null | undefined)?.code;
  const known = code ? BANK_ACCOUNT_ERROR_MESSAGES[code] : undefined;
  if (known) return known;
  if (err instanceof ApiError && err.status === 429) return TOO_MANY_REQUESTS_MESSAGE;
  return fallback;
}
